import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    const signal = this.#abortController.signal;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.loop = true;
    videoElement.load();

    this.addEventListener('pointerenter', this.handlePointerEnter, { signal });
    this.addEventListener('pointerleave', this.handlePointerLeave, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handlePointerEnter = () => {
    const { videoElement } = this.refs;

    videoElement.muted = true;
    videoElement.loop = true;

    const playPromise = videoElement.play();

    if (playPromise instanceof Promise) {
      playPromise.catch(() => {
        // Ignore autoplay rejections triggered by browser policies or rapid pointer movement.
      });
    }
  };

  handlePointerLeave = () => {
    const { videoElement } = this.refs;

    videoElement.pause();
    videoElement.currentTime = 0;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
